import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PlusCircle,
  Upload,
  FileText,
  Package,
  Tag,
  DollarSign,
  Hash,
  Percent,
  Globe,
  CheckCircle,
  XCircle,
  Image as ImageIcon,
  ArrowLeft,
  Save,
  Eye,
  EyeOff,
  BarChart3,
  Layers,
  Box,
  Scale,
  Calendar,
  Shield,
  Cpu,
  Briefcase,
  ChevronRight,
  ChevronLeft,
  Settings,
  FileCheck,
  AlertCircle,
  Truck,
  Grid,
  Zap,
  Sparkles,
  Palette,
  Hash as HashIcon,
  Ruler,
  Package as PackageIcon,
  Clock,
  ShieldCheck,
  ExternalLink,
  Copy,
  Download,
  Plus,
  Star,
  Filter,
  Search,
  List,
  TrendingUp,
  Users,
  BookOpen,
  Layout,
  FileText as FileTextIcon // Renamed to avoid conflict
} from 'lucide-react';
import Button from '../../../../components/common/Button';
import Input from '../../../../components/common/Input';
import Card from '../../../../components/ui/Card';
import Select from '../../../../components/common/Select';
import Textarea from '../../../../components/common/Textarea';

const AddProductDetails = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(1);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const [product, setProduct] = useState({
    // Basic Info
    name: '',
    sku: '',
    category: '',
    subCategory: '',
    brand: '',
    manufacturer: '',
    model: '',
    description: '',
    shortDescription: '',
    
    // Pricing
    price: '',
    comparePrice: '',
    costPrice: '',
    taxRate: '',
    discount: '',
    
    // Inventory
    stock: '',
    weight: '',
    dimensions: { length: '', width: '', height: '' },
    unit: 'pieces',
    moq: '1',
    leadTime: '7',
    reorderPoint: '10',
    maxStock: '',
    
    // Advanced
    warranty: '',
    countryOfOrigin: '',
    certifications: '',
    hazardous: false,
    fragile: false,
    temperatureControl: false,
    
    // Media
    images: [],
    videos: [],
    documents: [],
    
    // SEO & Marketing
    metaTitle: '',
    metaDescription: '',
    keywords: '',
    isFeatured: false,
    isNewArrival: false,
    
    // Shipping
    shippingWeight: '',
    shippingDimensions: { length: '', width: '', height: '' },
    shippingClass: 'standard',
    
    // Variants
    hasVariants: false,
    variants: [],
    
    // Custom Fields
    customAttributes: []
  });

  const steps = [
    { 
      id: 1, 
      name: 'Basic Info', 
      icon: Package,
      description: 'Core product details',
      color: 'blue'
    },
    { 
      id: 2, 
      name: 'Pricing', 
      icon: DollarSign,
      description: 'Price & offers',
      color: 'green'
    },
    { 
      id: 3, 
      name: 'Inventory', 
      icon: Box,
      description: 'Stock & shipping',
      color: 'orange'
    },
    { 
      id: 4, 
      name: 'Media', 
      icon: ImageIcon,
      description: 'Images & videos',
      color: 'purple'
    },
    { 
      id: 5, 
      name: 'Advanced', 
      icon: Cpu,
      description: 'Advanced settings',
      color: 'indigo'
    }
  ];

  const categories = [
    { value: 'electronics', label: 'Electronics', icon: '💻', color: 'blue' },
    { value: 'fashion', label: 'Fashion & Apparel', icon: '👕', color: 'pink' },
    { value: 'home', label: 'Home & Living', icon: '🏠', color: 'green' },
    { value: 'automotive', label: 'Automotive', icon: '🚗', color: 'red' },
    { value: 'industrial', label: 'Industrial', icon: '🏭', color: 'gray' },
    { value: 'health', label: 'Health & Beauty', icon: '💄', color: 'purple' },
    { value: 'sports', label: 'Sports & Outdoors', icon: '⚽', color: 'orange' },
    { value: 'office', label: 'Office Supplies', icon: '📎', color: 'yellow' }
  ];

  const shippingClasses = [
    { value: 'standard', label: 'Standard Shipping', delivery: '3-5 business days' },
    { value: 'express', label: 'Express Shipping', delivery: '1-2 business days' },
    { value: 'overnight', label: 'Overnight Shipping', delivery: 'Next business day' },
    { value: 'international', label: 'International', delivery: '7-14 business days' },
    { value: 'freight', label: 'Freight Shipping', delivery: 'Varies' }
  ];

  const units = [
    { value: 'pieces', label: 'Pieces' },
    { value: 'kg', label: 'Kilograms (kg)' },
    { value: 'g', label: 'Grams (g)' },
    { value: 'liters', label: 'Liters (L)' },
    { value: 'ml', label: 'Milliliters (ml)' },
    { value: 'meters', label: 'Meters (m)' },
    { value: 'cm', label: 'Centimeters (cm)' },
    { value: 'boxes', label: 'Boxes' },
    { value: 'sets', label: 'Sets' },
    { value: 'pairs', label: 'Pairs' }
  ];

  // Calculate completion percentage
  useEffect(() => {
    let filledFields = 0;
    let totalFields = 0;

    // Basic Info fields
    const basicFields = ['name', 'sku', 'category', 'description'];
    totalFields += basicFields.length;
    filledFields += basicFields.filter(field => product[field]?.trim()).length;

    // Pricing fields
    const pricingFields = ['price', 'costPrice'];
    totalFields += pricingFields.length;
    filledFields += pricingFields.filter(field => product[field]?.trim()).length;

    // Inventory fields
    const inventoryFields = ['stock', 'weight', 'unit'];
    totalFields += inventoryFields.length;
    filledFields += inventoryFields.filter(field => product[field]?.trim()).length;

    setCompletionPercentage(Math.round((filledFields / totalFields) * 100));
  }, [product]);

  const handleInputChange = (field, value) => {
    setProduct(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedChange = (parent, field, value) => {
    setProduct(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value
      }
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Product:', product);
    navigate('/seller/sales');
  };

  const nextStep = () => {
    if (activeStep < steps.length) {
      setActiveStep(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const prevStep = () => {
    if (activeStep > 1) {
      setActiveStep(prev => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const calculateProfitMargin = () => {
    if (!product.price || !product.costPrice) return '0.0';
    const margin = ((parseFloat(product.price) - parseFloat(product.costPrice)) / parseFloat(product.costPrice)) * 100;
    return margin.toFixed(1);
  };

  const calculateVolume = () => {
    const { length, width, height } = product.dimensions;
    if (!length || !width || !height) return '0.00';
    const volume = (parseFloat(length) * parseFloat(width) * parseFloat(height)) / 1000;
    return volume.toFixed(2);
  };

  // Format currency in INR
  const formatINR = (amount) => {
    if (!amount) return '₹0.00';
    return `₹${parseFloat(amount).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      {/* Header with Progress */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-6 text-white">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              <Button
                variant="ghost"
                size="small"
                onClick={() => navigate('/seller/sales')}
                className="bg-white/10 hover:bg-white/20 text-white border-0 flex items-center justify-center"
              >
                <ArrowLeft className="h-4 w-4 mr-2 flex-shrink-0" />
                Back to Sales
              </Button>
              <span className="px-3 py-1 bg-white/20 rounded-full text-xs font-medium">
                {steps[activeStep - 1]?.name}
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold mb-2">Add New Product</h1>
            <p className="text-blue-100">Create professional product listings with detailed specifications</p>
          </div>
          
          <div className="flex flex-col items-end gap-3">
            <div className="text-right">
              <div className="text-3xl font-bold">{completionPercentage}%</div>
              <div className="text-sm text-blue-100">Completion</div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="small"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="bg-white/10 hover:bg-white/20 text-white border-white/30 flex items-center justify-center"
              >
                <Settings className="h-4 w-4 mr-2 flex-shrink-0" />
                {showAdvanced ? 'Hide Advanced' : 'Advanced'}
              </Button>
              <Button 
                type="submit" 
                form="product-form"
                className="bg-white text-blue-600 hover:bg-blue-50 border-0 shadow-lg flex items-center justify-center"
              >
                <Save className="h-4 w-4 mr-2 flex-shrink-0" />
                Save Draft
              </Button>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Progress</span>
            <span className="text-sm font-medium">Step {activeStep} of {steps.length}</span>
          </div>
          <div className="h-2 bg-white/20 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-cyan-400 to-blue-400 transition-all duration-500"
              style={{ width: `${(activeStep / steps.length) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Quick Actions Bar */}
      <div className="flex flex-wrap items-center gap-3 p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
        <Button
          variant="outline"
          size="small"
          onClick={() => navigate('/seller/sales/product-templates')}
          className="flex items-center justify-center"
        >
          <FileTextIcon className="h-4 w-4 mr-2 flex-shrink-0" />
          Use Template
        </Button>
        <Button
          variant="outline"
          size="small"
          onClick={() => navigate('/seller/sales/bulk-upload')}
          className="flex items-center justify-center"
        >
          <Upload className="h-4 w-4 mr-2 flex-shrink-0" />
          Bulk Upload
        </Button>
        <Button
          variant="outline"
          size="small"
          onClick={() => window.open('/product-guidelines', '_blank')}
          className="flex items-center justify-center"
        >
          <BookOpen className="h-4 w-4 mr-2 flex-shrink-0" />
          Guidelines
        </Button>
      </div>

      {/* Steps Navigation */}
      <Card className="overflow-hidden border-0 shadow-lg">
        <div className="p-4 bg-gray-50 border-b">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {steps.map((step) => {
              const Icon = step.icon;
              const isActive = activeStep === step.id;
              const isCompleted = activeStep > step.id;
              
              return (
                <button
                  key={step.id}
                  onClick={() => setActiveStep(step.id)}
                  className={`flex items-center p-3 rounded-xl transition-all duration-300 ${
                    isActive 
                      ? `bg-${step.color}-50 border-2 border-${step.color}-200 shadow-sm` 
                      : isCompleted
                      ? 'bg-green-50 border-2 border-green-200'
                      : 'bg-white border-2 border-gray-100 hover:border-gray-200'
                  }`}
                >
                  <div className={`p-2 rounded-lg mr-3 flex-shrink-0 ${
                    isActive 
                      ? `bg-${step.color}-100 text-${step.color}-600` 
                      : isCompleted
                      ? 'bg-green-100 text-green-600'
                      : 'bg-gray-100 text-gray-500'
                  }`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="text-left flex-1 min-w-0">
                    <div className={`text-sm font-semibold truncate ${
                      isActive ? 'text-gray-900' : 
                      isCompleted ? 'text-green-700' : 
                      'text-gray-600'
                    }`}>
                      {step.name}
                    </div>
                    <div className="text-xs text-gray-500 truncate">{step.description}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </Card>

      <form id="product-form" onSubmit={handleSubmit} className="space-y-8">
        {/* Step 1: Basic Information */}
        {activeStep === 1 && (
          <div className="space-y-8">
            {/* Product Identity */}
            <Card className="border-0 shadow-lg">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-r from-blue-100 to-cyan-100 rounded-xl flex-shrink-0">
                      <Package className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">Product Identity</h3>
                      <p className="text-sm text-gray-600 truncate">Core details that identify your product</p>
                    </div>
                  </div>
                  <span className="px-3 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full flex-shrink-0">
                    Required Fields
                  </span>
                </div>
              </div>
              
              <div className="p-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="lg:col-span-2">
                    <Input
                      label="Product Name"
                      value={product.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="Enter descriptive product title"
                      required
                      leftIcon={<Tag className="h-5 w-5 text-gray-400" />}
                      helperText="Be descriptive and include key features"
                      className="text-lg"
                    />
                  </div>
                  
                  <div className="lg:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      Product Category
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      {categories.map((cat) => (
                        <button
                          key={cat.value}
                          type="button"
                          onClick={() => handleInputChange('category', cat.value)}
                          className={`flex flex-col items-center p-5 rounded-xl border-2 transition-all duration-200 ${
                            product.category === cat.value
                              ? `border-${cat.color}-500 bg-${cat.color}-50 shadow-sm`
                              : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                          }`}
                        >
                          <span className="text-3xl mb-3">{cat.icon}</span>
                          <span className="text-sm font-medium text-gray-900">{cat.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <Input
                        label="SKU Code"
                        value={product.sku}
                        onChange={(e) => handleInputChange('sku', e.target.value)}
                        placeholder="PROD-2024-001"
                        required
                        leftIcon={<HashIcon className="h-5 w-5 text-gray-400" />}
                        helperText="Unique inventory identifier"
                      />
                      
                      <Input
                        label="Model Number"
                        value={product.model}
                        onChange={(e) => handleInputChange('model', e.target.value)}
                        placeholder="MOD-2024"
                        leftIcon={<Hash className="h-5 w-5 text-gray-400" />}
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <Input
                        label="Brand"
                        value={product.brand}
                        onChange={(e) => handleInputChange('brand', e.target.value)}
                        placeholder="Brand name"
                        leftIcon={<Briefcase className="h-5 w-5 text-gray-400" />}
                      />
                      
                      <Input
                        label="Manufacturer"
                        value={product.manufacturer}
                        onChange={(e) => handleInputChange('manufacturer', e.target.value)}
                        placeholder="Manufacturer name"
                        leftIcon={<Layers className="h-5 w-5 text-gray-400" />}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-6">
                    <Textarea
                      label="Short Description"
                      value={product.shortDescription}
                      onChange={(e) => handleInputChange('shortDescription', e.target.value)}
                      placeholder="Brief overview that appears in product listings and search results..."
                      rows={4}
                      helperText="Maximum 200 characters"
                      maxLength={200}
                    />
                    
                    <div className="flex items-start text-sm text-gray-500">
                      <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" />
                      <span>This description appears in search results and product cards</span>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Product Description */}
            <Card className="border-0 shadow-lg">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gradient-to-r from-green-100 to-emerald-100 rounded-xl flex-shrink-0">
                    <FileText className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 truncate">Detailed Description</h3>
                    <p className="text-sm text-gray-600 truncate">Complete product description with features and specifications</p>
                  </div>
                </div>
              </div>
              
              <div className="p-8">
                <Textarea
                  label="Full Product Description"
                  value={product.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Describe your product in detail. Include features, benefits, specifications, usage instructions, and any other relevant information..."
                  rows={8}
                  required
                  helperText="Use rich text formatting. Include bullet points and paragraphs for better readability."
                />
                
                <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-100">
                  <div className="flex items-start gap-3">
                    <Zap className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-blue-900">Pro Tip</p>
                      <p className="text-sm text-blue-700">
                        Include key features, specifications, and benefits. Use bullet points for better readability. 
                        Good descriptions improve search ranking and customer conversion.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Step 2: Pricing */}
        {activeStep === 2 && (
          <div className="space-y-8">
            <Card className="border-0 shadow-lg">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-r from-green-100 to-emerald-100 rounded-xl flex-shrink-0">
                      <DollarSign className="h-6 w-6 text-green-600" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">Pricing Strategy</h3>
                      <p className="text-sm text-gray-600 truncate">Set competitive pricing and promotional offers</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                  <div className="space-y-6">
                    <div className="relative">
                      <Input
                        label="Selling Price"
                        type="number"
                        value={product.price}
                        onChange={(e) => handleInputChange('price', e.target.value)}
                        placeholder="0.00"
                        required
                        min="0"
                        step="0.01"
                        leftIcon={<span className="text-gray-400 font-medium">₹</span>}
                      />
                      <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-semibold">
                        Primary
                      </div>
                    </div>
                    <div className="text-sm text-gray-600">
                      {product.price ? formatINR(product.price) : 'Enter price'}
                    </div>
                  </div>
                  
                  <div className="space-y-6">
                    <Input
                      label="Compare At Price"
                      type="number"
                      value={product.comparePrice}
                      onChange={(e) => handleInputChange('comparePrice', e.target.value)}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      leftIcon={<span className="text-gray-400 font-medium">₹</span>}
                      helperText="Shows as 'Original Price' to create discount perception"
                    />
                    {product.price && product.comparePrice && (
                      <div className="text-sm font-medium text-red-600">
                        Discount: {(((parseFloat(product.comparePrice) - parseFloat(product.price)) / parseFloat(product.comparePrice)) * 100).toFixed(0)}%
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-6">
                    <Input
                      label="Cost Price"
                      type="number"
                      value={product.costPrice}
                      onChange={(e) => handleInputChange('costPrice', e.target.value)}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      leftIcon={<span className="text-gray-400 font-medium">₹</span>}
                      helperText="Your purchase/manufacturing cost"
                    />
                  </div>
                  
                  <div className="space-y-6">
                    <Input
                      label="Tax Rate"
                      type="number"
                      value={product.taxRate}
                      onChange={(e) => handleInputChange('taxRate', e.target.value)}
                      placeholder="0"
                      min="0"
                      max="100"
                      step="0.1"
                      rightIcon={<Percent className="h-4 w-4 text-gray-400" />}
                      helperText="Applied to selling price"
                    />
                  </div>
                </div>
                
                {/* Profit Margin Calculator */}
                <div className="mt-8 p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-100">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-gray-900">
                        {calculateProfitMargin()}%
                      </div>
                      <div className="text-sm text-gray-600">Profit Margin</div>
                    </div>
                    
                    <div className="text-center">
                      <div className="text-3xl font-bold text-gray-900">
                        {product.price && product.costPrice 
                          ? formatINR(parseFloat(product.price) - parseFloat(product.costPrice))
                          : formatINR(0)
                        }
                      </div>
                      <div className="text-sm text-gray-600">Profit per Unit</div>
                    </div>
                    
                    <div className="text-center">
                      <div className="text-3xl font-bold text-gray-900">
                        {product.stock && product.price && product.costPrice
                          ? formatINR(parseFloat(product.stock) * (parseFloat(product.price) - parseFloat(product.costPrice)))
                          : formatINR(0)
                        }
                      </div>
                      <div className="text-sm text-gray-600">Total Potential Profit</div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Step 3: Inventory & Shipping */}
        {activeStep === 3 && (
          <div className="space-y-8">
            <Card className="border-0 shadow-lg">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-r from-orange-100 to-amber-100 rounded-xl flex-shrink-0">
                      <Box className="h-6 w-6 text-orange-600" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">Inventory Management</h3>
                      <p className="text-sm text-gray-600 truncate">Stock control and replenishment settings</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                  <div className="space-y-2">
                    <Input
                      label="Current Stock"
                      type="number"
                      value={product.stock}
                      onChange={(e) => handleInputChange('stock', e.target.value)}
                      placeholder="0"
                      required
                      min="0"
                      helperText="Available units"
                    />
                    <div className="text-xs text-gray-500">
                      {product.stock > 0 ? 'In Stock' : 'Out of Stock'}
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Input
                      label="Reorder Point"
                      type="number"
                      value={product.reorderPoint}
                      onChange={(e) => handleInputChange('reorderPoint', e.target.value)}
                      placeholder="10"
                      min="0"
                      helperText="Trigger for restock alerts"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Input
                      label="Max Stock Capacity"
                      type="number"
                      value={product.maxStock}
                      onChange={(e) => handleInputChange('maxStock', e.target.value)}
                      placeholder="1000"
                      min="0"
                      helperText="Maximum storage capacity"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Select
                      label="Unit of Measure"
                      value={product.unit}
                      onChange={(value) => handleInputChange('unit', value)}
                      options={units}
                    />
                  </div>
                </div>
                
                <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Dimensions */}
                  <div className="space-y-4">
                    <label className="block text-sm font-semibold text-gray-700">
                      Product Dimensions (cm)
                    </label>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Input
                          label="Length"
                          value={product.dimensions.length}
                          onChange={(e) => handleNestedChange('dimensions', 'length', e.target.value)}
                          placeholder="0.00"
                          type="number"
                          min="0"
                          step="0.01"
                          leftIcon={<Ruler className="h-5 w-5 text-gray-400" />}
                        />
                      </div>
                      <div className="space-y-2">
                        <Input
                          label="Width"
                          value={product.dimensions.width}
                          onChange={(e) => handleNestedChange('dimensions', 'width', e.target.value)}
                          placeholder="0.00"
                          type="number"
                          min="0"
                          step="0.01"
                        />
                      </div>
                      <div className="space-y-2">
                        <Input
                          label="Height"
                          value={product.dimensions.height}
                          onChange={(e) => handleNestedChange('dimensions', 'height', e.target.value)}
                          placeholder="0.00"
                          type="number"
                          min="0"
                          step="0.01"
                        />
                      </div>
                    </div>
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-700">Total Volume:</span>
                        <span className="text-sm font-semibold text-blue-700">
                          {calculateVolume()} liters
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Weight & Lead Time */}
                  <div className="space-y-6">
                    <Input
                      label="Product Weight (kg)"
                      type="number"
                      value={product.weight}
                      onChange={(e) => handleInputChange('weight', e.target.value)}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      leftIcon={<Scale className="h-5 w-5 text-gray-400" />}
                      helperText="Used for shipping calculations"
                    />
                    
                    <Input
                      label="Lead Time (Days)"
                      type="number"
                      value={product.leadTime}
                      onChange={(e) => handleInputChange('leadTime', e.target.value)}
                      placeholder="7"
                      min="1"
                      leftIcon={<Clock className="h-5 w-5 text-gray-400" />}
                      helperText="Processing time before shipping"
                    />
                    
                    <Input
                      label="Minimum Order Quantity"
                      type="number"
                      value={product.moq}
                      onChange={(e) => handleInputChange('moq', e.target.value)}
                      placeholder="1"
                      min="1"
                      leftIcon={<PackageIcon className="h-5 w-5 text-gray-400" />}
                    />
                  </div>
                </div>
              </div>
            </Card>

            {/* Shipping Configuration */}
            <Card className="border-0 shadow-lg">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gradient-to-r from-blue-100 to-cyan-100 rounded-xl flex-shrink-0">
                    <Truck className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 truncate">Shipping Configuration</h3>
                    <p className="text-sm text-gray-600 truncate">Define shipping rules and restrictions</p>
                  </div>
                </div>
              </div>
              
              <div className="p-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div>
                    <Select
                      label="Shipping Class"
                      value={product.shippingClass}
                      onChange={(value) => handleInputChange('shippingClass', value)}
                      options={shippingClasses.map(sc => ({
                        value: sc.value,
                        label: `${sc.label} (${sc.delivery})`
                      }))}
                    />
                    
                    <div className="mt-6 p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <ShieldCheck className="h-5 w-5 text-green-600 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">Shipping Protection</p>
                          <p className="text-xs text-gray-600 truncate">
                            Fragile items require special packaging and handling
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <Input
                      label="Shipping Weight (kg)"
                      type="number"
                      value={product.shippingWeight}
                      onChange={(e) => handleInputChange('shippingWeight', e.target.value)}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      helperText="May differ from product weight due to packaging"
                    />
                    
                    <div className="mt-6 flex flex-wrap gap-4">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={product.fragile}
                          onChange={(e) => handleInputChange('fragile', e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">Fragile Item</span>
                      </label>
                      
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={product.hazardous}
                          onChange={(e) => handleInputChange('hazardous', e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">Hazardous Material</span>
                      </label>
                      
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={product.temperatureControl}
                          onChange={(e) => handleInputChange('temperatureControl', e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">Temperature Control</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Advanced Options (Always visible if enabled) */}
        {showAdvanced && (
          <Card className="border-0 shadow-lg">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gradient-to-r from-indigo-100 to-purple-100 rounded-xl flex-shrink-0">
                    <Cpu className="h-6 w-6 text-indigo-600" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 truncate">Advanced Configuration</h3>
                    <p className="text-sm text-gray-600 truncate">Additional settings for power users</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="small"
                  onClick={() => setShowAdvanced(false)}
                  className="text-gray-600 hover:text-gray-900 flex items-center justify-center"
                >
                  <XCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                  Collapse
                </Button>
              </div>
            </div>
            
            <div className="p-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Shield className="h-5 w-5 text-blue-600 flex-shrink-0" />
                    Product Compliance
                  </h4>
                  
                  <Input
                    label="Warranty Period"
                    value={product.warranty}
                    onChange={(e) => handleInputChange('warranty', e.target.value)}
                    placeholder="e.g., 1 Year Limited Warranty"
                    leftIcon={<ShieldCheck className="h-5 w-5 text-gray-400" />}
                  />
                  
                  <Input
                    label="Country of Origin"
                    value={product.countryOfOrigin}
                    onChange={(e) => handleInputChange('countryOfOrigin', e.target.value)}
                    placeholder="e.g., China, Germany, USA"
                    leftIcon={<Globe className="h-5 w-5 text-gray-400" />}
                  />
                  
                  <Textarea
                    label="Certifications & Standards"
                    value={product.certifications}
                    onChange={(e) => handleInputChange('certifications', e.target.value)}
                    placeholder="ISO 9001, CE Mark, RoHS, FCC, etc."
                    rows={3}
                    helperText="Separate certifications with commas"
                  />
                </div>
                
                <div className="space-y-6">
                  <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-purple-600 flex-shrink-0" />
                    SEO & Marketing
                  </h4>
                  
                  <Input
                    label="Meta Title"
                    value={product.metaTitle}
                    onChange={(e) => handleInputChange('metaTitle', e.target.value)}
                    placeholder="Optimized title for search engines"
                    helperText="Recommended: 50-60 characters"
                  />
                  
                  <Textarea
                    label="Meta Description"
                    value={product.metaDescription}
                    onChange={(e) => handleInputChange('metaDescription', e.target.value)}
                    placeholder="Brief description for search engine results"
                    rows={2}
                    helperText="Recommended: 150-160 characters"
                  />
                  
                  <Input
                    label="Keywords"
                    value={product.keywords}
                    onChange={(e) => handleInputChange('keywords', e.target.value)}
                    placeholder="comma, separated, keywords"
                    helperText="For better search visibility"
                  />
                  
                  <div className="flex flex-wrap gap-6">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={product.isFeatured}
                        onChange={(e) => handleInputChange('isFeatured', e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Featured Product</span>
                    </label>
                    
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={product.isNewArrival}
                        onChange={(e) => handleInputChange('isNewArrival', e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">New Arrival</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Navigation Controls */}
        <div className="sticky bottom-6 bg-white border border-gray-200 rounded-2xl shadow-xl p-6">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex flex-wrap items-center gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/seller/sales')}
                className="min-w-[140px] border-gray-300 hover:border-gray-400 flex items-center justify-center"
              >
                Cancel
              </Button>
              
              {activeStep > 1 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={prevStep}
                  className="min-w-[140px] flex items-center justify-center"
                >
                  <ChevronLeft className="h-4 w-4 mr-2 flex-shrink-0" />
                  Previous Step
                </Button>
              )}
            </div>
            
            <div className="flex flex-wrap items-center gap-3">
              {activeStep < steps.length ? (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/seller/sales')}
                    className="min-w-[140px] flex items-center justify-center"
                  >
                    Save as Draft
                  </Button>
                  <Button
                    type="button"
                    onClick={nextStep}
                    className="min-w-[140px] bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 flex items-center justify-center"
                  >
                    Next Step
                    <ChevronRight className="h-4 w-4 ml-2 flex-shrink-0" />
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    className="min-w-[140px] flex items-center justify-center"
                  >
                    <Settings className="h-4 w-4 mr-2 flex-shrink-0" />
                    {showAdvanced ? 'Hide Advanced' : 'Advanced'}
                  </Button>
                  <Button
                    type="submit"
                    className="min-w-[140px] bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 flex items-center justify-center"
                  >
                    <CheckCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                    Publish Product
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </form>

      {/* Quick Stats & Help */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-gray-500">Completion Status</p>
              <p className="text-2xl font-bold text-gray-900">{completionPercentage}%</p>
            </div>
            <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-100 to-purple-100 flex items-center justify-center flex-shrink-0">
              <CheckCircle className="h-6 w-6 text-blue-600" />
            </div>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-500"
              style={{ width: `${completionPercentage}%` }}
            />
          </div>
        </Card>
        
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-gray-500">Current Step</p>
              <p className="text-xl font-bold text-gray-900">
                {steps[activeStep - 1]?.name}
              </p>
              <p className="text-xs text-gray-500">{steps[activeStep - 1]?.description}</p>
            </div>
            <div className="w-12 h-12 rounded-full bg-gradient-to-r from-green-100 to-emerald-100 flex items-center justify-center flex-shrink-0">
              <div className="text-lg font-bold text-green-600">{activeStep}</div>
            </div>
          </div>
        </Card>
        
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Need Help?</p>
              <p className="text-sm font-medium text-gray-900 mb-2">Quick Actions</p>
            </div>
            <ExternalLink className="h-5 w-5 text-gray-400 flex-shrink-0" />
          </div>
          <div className="space-y-2 mt-3">
            <Button 
              variant="ghost" 
              size="small" 
              fullWidth 
              className="justify-start flex items-center"
              onClick={() => navigate('/seller/sales/product-templates')}
            >
              <FileTextIcon className="h-4 w-4 mr-2 flex-shrink-0" />
              Use Template
            </Button>
            <Button 
              variant="ghost" 
              size="small" 
              fullWidth 
              className="justify-start flex items-center"
              onClick={() => navigate('/seller/sales/bulk-upload')}
            >
              <Upload className="h-4 w-4 mr-2 flex-shrink-0" />
              Bulk Upload
            </Button>
            <Button 
              variant="ghost" 
              size="small" 
              fullWidth 
              className="justify-start flex items-center"
              onClick={() => window.open('/product-guidelines', '_blank')}
            >
              <BookOpen className="h-4 w-4 mr-2 flex-shrink-0" />
              View Guidelines
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AddProductDetails;